import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit } from "@angular/core";
import { ServerTokenService } from "../../services/server-token.service";
import { FacilityService } from "../../manageb/facility.service";
import { CustomAuthServiceService } from "../../auth/custom-auth-service.service";
import { catchError, finalize, tap } from "rxjs/operators";
import { of } from "rxjs";

interface BedSetupRow {
  _id?: string;
  bed_id: string;
  fac_id: number;
  facility_name: string;
  regionId: number;
  unit_id: string;
  unit_name: string;
  room_number: string;
  bed_desc: string;
  room_type: string;
  status: "active" | "not-active";
}

interface BedSetupData {
  facility_name: string;
  fac_id: number;
  regionId: number;
  sum_beds: number;
  units: string[];
  units_map: Record<string, string>; // key=PCC name (unit_id), value=display name
  beds: BedSetupRow[];
}

interface UnitGroup {
  unitId: string;
  unitName: string;
  beds: BedSetupRow[];
  activeCount: number;
  notActiveCount: number;
}

@Component({
  selector: "bed-setup-settings",
  templateUrl: "./bed-setup-settings.component.html",
  styleUrls: ["./bed-setup-settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BedSetupSettingsComponent implements OnInit {
  facilityMaps: any[] = [];
  selectedFacilityId: number | null = null;

  bedSetupData: BedSetupData | null = null;
  unitGroups: UnitGroup[] = [];
  loading = false;
  error = "";
  successMsg = "";

  // Popup state
  popupOpen = false;
  popupMode: "edit" | "add" | "import" = "edit";
  popupBed: BedSetupRow | null = null;
  editRoomType = "";
  editStatus: "active" | "not-active" = "active";

  newBed = { unit_name: "", room_number: "", bed_desc: "" };

  // Inline edit state
  editingBedId: string | null = null;
  editingField: string | null = null;

  // Unit rename state
  editingUnitId: string | null = null;
  editingUnitName = "";

  // Row menu state
  activeRowMenu: string | null = null;

  // Import popup state
  importFiles: { file: File; fac_id: number; fileName: string }[] = [];
  importRegionId: number | null = null;
  importReplaceExisting = false;

  readonly roomTypes = ["", "Private", "Semi-Private", "Ward"];
  readonly statuses: Array<"active" | "not-active"> = ["active", "not-active"];

  constructor(
    private server: ServerTokenService,
    private facilityService: FacilityService,
    private auth: CustomAuthServiceService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    await this.facilityService.waitUntilReady();

    const regionId = this.auth.currentUserValue()?.regionId;
    if (regionId != null) {
      this.importRegionId = Number(regionId);
    }

    this.loadFacilitiesWithBedSetup();
  }

  private loadFacilitiesWithBedSetup() {
    this.server
      .getWithToken<any[]>("snfs/admin/getAllFacilityBedsMaps")
      .pipe(
        tap((maps) => {
          console.log('bed-setup: getAllFacilityBedsMaps response =', maps);
          this.facilityMaps = maps || [];
        }),
        catchError((err) => {
          console.error('bed-setup: getAllFacilityBedsMaps ERROR =', err);
          this.facilityMaps = [];
          return of(null);
        }),
        finalize(() => this.cdr.markForCheck())
      )
      .subscribe();
  }

  get activeCount(): number {
    return this.bedSetupData?.beds?.filter((b) => b.status === "active").length ?? 0;
  }

  get notActiveCount(): number {
    return this.bedSetupData?.beds?.filter((b) => b.status === "not-active").length ?? 0;
  }

  get roomTypeCount(): number {
    return this.bedSetupData?.beds?.filter((b) => b.room_type).length ?? 0;
  }

  onFacilityChange(facId: string) {
    this.selectedFacilityId = Number(facId) || null;
    if (this.selectedFacilityId) {
      this.loadBedSetup();
    } else {
      this.bedSetupData = null;
      this.unitGroups = [];
    }
  }

  loadBedSetup() {
    if (!this.selectedFacilityId) return;
    this.loading = true;
    this.error = "";
    console.log('getBedSetup REQ:', { fac_id: this.selectedFacilityId });
    this.server
      .postWithToken<BedSetupData>("snfs/admin/getBedSetup", {
        fac_id: this.selectedFacilityId,
      })
      .pipe(
        tap((data) => {
          console.log('getBedSetup RES:', data);
          this.bedSetupData = data;
          this.unitGroups = this.groupByUnit(data?.beds || []);
        }),
        catchError((err) => {
          console.error('getBedSetup ERR:', err);
          this.error = "Failed to load bed setup data.";
          this.bedSetupData = null;
          this.unitGroups = [];
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private groupByUnit(beds: BedSetupRow[]): UnitGroup[] {
    const unitsMap = this.bedSetupData?.units_map || {};
    console.log('groupByUnit: total beds =', beds.length, 'units_map =', unitsMap);
    const map = new Map<string, BedSetupRow[]>();
    beds.forEach((bed) => {
      const key = bed.unit_id || bed.unit_name;
      const list = map.get(key) || [];
      list.push(bed);
      map.set(key, list);
    });
    return Array.from(map.entries()).map(([unitId, unitBeds]) => ({
      unitId,
      unitName: unitsMap[unitId] || unitId, // display name from map, fallback to PCC name
      beds: unitBeds.sort((a, b) =>
        (a.room_number + a.bed_desc).localeCompare(b.room_number + b.bed_desc)
      ),
      activeCount: unitBeds.filter((b) => b.status === "active").length,
      notActiveCount: unitBeds.filter((b) => b.status === "not-active").length,
    }));
  }

  // ---- Unit rename (dblclick on unit header) ----

  startUnitRename(group: UnitGroup) {
    this.editingUnitId = group.unitId;
    this.editingUnitName = group.unitName;
    this.cdr.markForCheck();
    setTimeout(() => {
      const input = document.querySelector('.unit-name-editing input') as HTMLInputElement;
      input?.focus();
      input?.select();
    });
  }

  saveUnitRename(group: UnitGroup) {
    const newName = this.editingUnitName.trim();
    if (!newName || newName === group.unitName || !this.selectedFacilityId) {
      this.cancelUnitRename();
      return;
    }
    console.log('renameUnit REQ:', { fac_id: this.selectedFacilityId, unit_id: group.unitId, new_unit_name: newName });
    this.server
      .postWithToken<any>("snfs/admin/renameUnit", {
        fac_id: this.selectedFacilityId,
        unit_id: group.unitId,
        new_unit_name: newName,
      })
      .pipe(
        tap((res) => {
          console.log('renameUnit RES:', res);
          this.cancelUnitRename();
          this.loadBedSetup();
        }),
        catchError((err) => {
          console.error('renameUnit ERR:', err);
          this.error = "Failed to rename unit.";
          this.cancelUnitRename();
          return of(null);
        }),
        finalize(() => this.cdr.markForCheck())
      )
      .subscribe();
  }

  cancelUnitRename() {
    this.editingUnitId = null;
    this.editingUnitName = "";
    this.cdr.markForCheck();
  }

  // ---- Inline edit (click to reveal dropdown) ----

  startInlineEdit(bed: BedSetupRow, field: string) {
    this.editingBedId = bed.bed_id;
    this.editingField = field;
    this.cdr.markForCheck();
    setTimeout(() => {
      const el = document.querySelector('.inline-select:focus-within, .inline-select') as HTMLSelectElement;
      el?.focus();
    });
  }

  clearInlineEdit() {
    setTimeout(() => {
      this.editingBedId = null;
      this.editingField = null;
      this.cdr.markForCheck();
    }, 100);
  }

  onStatusChange(bed: BedSetupRow, newStatus: string) {
    this.updateBed(bed.bed_id, { status: newStatus as "active" | "not-active" });
  }

  onRoomTypeChange(bed: BedSetupRow, newRoomType: string) {
    const trimmed = newRoomType.trim();
    if (trimmed === bed.room_type) return;
    this.updateBed(bed.bed_id, { room_type: trimmed });
  }

  private updateBed(bed_id: string, updates: { status?: string; room_type?: string }) {
    this.error = "";
    console.log('updateBed REQ:', { bed_id, updates });
    this.server
      .postWithToken<BedSetupRow>("snfs/admin/updateBed", { bed_id, updates })
      .pipe(
        tap((updated) => {
          console.log('updateBed RES:', updated);
          if (this.bedSetupData) {
            const beds = this.bedSetupData.beds.map((b) =>
              b.bed_id === updated.bed_id ? { ...b, ...updated } : b
            );
            this.bedSetupData = { ...this.bedSetupData, beds };
            this.unitGroups = this.groupByUnit(beds);
          }
        }),
        catchError((err) => {
          console.error('updateBed ERR:', err);
          this.error = "Failed to update bed.";
          return of(null);
        }),
        finalize(() => this.cdr.markForCheck())
      )
      .subscribe();
  }

  deleteBedInline(bed: BedSetupRow) {
    if (!confirm(`Delete bed ${bed.room_number}-${bed.bed_desc} in ${bed.unit_name}?`)) return;
    console.log('deleteBed (inline) REQ:', { bed_id: bed.bed_id });
    this.server
      .postWithToken<any>("snfs/admin/deleteBed", { bed_id: bed.bed_id })
      .pipe(
        tap((res) => {
          console.log('deleteBed (inline) RES:', res);
          this.showSuccess("Bed deleted.");
          this.loadBedSetup();
          this.loadFacilitiesWithBedSetup();
        }),
        catchError((err) => {
          console.error('deleteBed (inline) ERR:', err);
          this.error = "Failed to delete bed.";
          return of(null);
        }),
        finalize(() => this.cdr.markForCheck())
      )
      .subscribe();
  }

  // ---- Sum Beds ----

  onSumBedsChange(value: string) {
    if (!this.selectedFacilityId) return;
    const sumBeds = Number(value);
    if (isNaN(sumBeds) || sumBeds < 0) return;
    console.log('updateFacilityBedsMap REQ:', { fac_id: this.selectedFacilityId, updates: { sum_beds: sumBeds } });
    this.server
      .postWithToken<any>("snfs/admin/updateFacilityBedsMap", {
        fac_id: this.selectedFacilityId,
        updates: { sum_beds: sumBeds },
      })
      .pipe(
        tap((updated) => {
          console.log('updateFacilityBedsMap RES:', updated);
          if (this.bedSetupData) {
            this.bedSetupData = { ...this.bedSetupData, sum_beds: updated.sum_beds };
          }
          this.showSuccess("Sum beds updated.");
          this.loadFacilitiesWithBedSetup();
        }),
        catchError((err) => {
          console.error('updateFacilityBedsMap ERR:', err);
          this.error = "Failed to update sum beds.";
          return of(null);
        }),
        finalize(() => this.cdr.markForCheck())
      )
      .subscribe();
  }

  // ---- Popup ----

  openEditPopup(bed: BedSetupRow) {
    this.popupBed = bed;
    this.editRoomType = bed.room_type;
    this.editStatus = bed.status;
    this.popupMode = "edit";
    this.popupOpen = true;
    this.cdr.markForCheck();
  }

  openAddPopup() {
    this.newBed = { unit_name: "", room_number: "", bed_desc: "" };
    this.popupMode = "add";
    this.popupOpen = true;
    this.cdr.markForCheck();
  }

  // ---- Row menu (add above / add below) ----

  @HostListener("document:click")
  onDocumentClick() {
    if (this.activeRowMenu) {
      this.activeRowMenu = null;
      this.cdr.markForCheck();
    }
  }

  toggleRowMenu(bedId: string, event: Event) {
    event.stopPropagation();
    this.activeRowMenu = this.activeRowMenu === bedId ? null : bedId;
    this.cdr.markForCheck();
  }

  closeRowMenu() {
    this.activeRowMenu = null;
    this.cdr.markForCheck();
  }

  openDuplicateBedPopup(bed: BedSetupRow, event: Event) {
    event.stopPropagation();
    this.newBed = {
      unit_name: bed.unit_name,
      room_number: bed.room_number,
      bed_desc: bed.bed_desc,
    };
    this.popupMode = "add";
    this.popupOpen = true;
    this.activeRowMenu = null;
    this.cdr.markForCheck();
  }

  closePopup() {
    this.popupOpen = false;
    this.popupBed = null;
    this.cdr.markForCheck();
  }

  saveEdit() {
    if (!this.popupBed) return;
    const updates: any = {};
    if (this.editRoomType !== this.popupBed.room_type) updates.room_type = this.editRoomType;
    if (this.editStatus !== this.popupBed.status) updates.status = this.editStatus;
    if (Object.keys(updates).length === 0) {
      this.closePopup();
      return;
    }
    console.log('updateBed (popup) REQ:', { bed_id: this.popupBed.bed_id, updates });
    this.server
      .postWithToken<BedSetupRow>("snfs/admin/updateBed", {
        bed_id: this.popupBed.bed_id,
        updates,
      })
      .pipe(
        tap((updated) => {
          console.log('updateBed (popup) RES:', updated);
          if (this.bedSetupData) {
            const beds = this.bedSetupData.beds.map((b) =>
              b.bed_id === updated.bed_id ? { ...b, ...updated } : b
            );
            this.bedSetupData = { ...this.bedSetupData, beds };
            this.unitGroups = this.groupByUnit(beds);
          }
          this.closePopup();
          this.showSuccess("Bed updated.");
        }),
        catchError((err) => {
          console.error('updateBed (popup) ERR:', err);
          this.error = "Failed to update bed.";
          return of(null);
        }),
        finalize(() => this.cdr.markForCheck())
      )
      .subscribe();
  }

  deleteBed() {
    if (!this.popupBed) return;
    if (!confirm(`Delete bed ${this.popupBed.room_number}-${this.popupBed.bed_desc} in ${this.popupBed.unit_name}?`)) return;

    console.log('deleteBed (popup) REQ:', { bed_id: this.popupBed.bed_id });
    this.server
      .postWithToken<any>("snfs/admin/deleteBed", { bed_id: this.popupBed.bed_id })
      .pipe(
        tap((res) => {
          console.log('deleteBed (popup) RES:', res);
          this.closePopup();
          this.showSuccess("Bed deleted.");
          this.loadBedSetup();
          this.loadFacilitiesWithBedSetup();
        }),
        catchError((err) => {
          console.error('deleteBed (popup) ERR:', err);
          this.error = "Failed to delete bed.";
          return of(null);
        }),
        finalize(() => this.cdr.markForCheck())
      )
      .subscribe();
  }

  addBed() {
    if (!this.selectedFacilityId || !this.newBed.unit_name || !this.newBed.room_number || !this.newBed.bed_desc) {
      this.error = "All fields are required.";
      this.cdr.markForCheck();
      return;
    }
    const facilityName = this.bedSetupData?.facility_name || "";
    const csvLine = `unit_desc,room_desc,bed_desc,facility_name\n${this.newBed.unit_name},${this.newBed.room_number},${this.newBed.bed_desc},${facilityName}`;

    console.log('addBed (importBedSetupCsv) REQ:', { fac_id: this.selectedFacilityId, csvString: csvLine });
    this.server
      .postWithToken<any>("snfs/admin/importBedSetupCsv", {
        fac_id: this.selectedFacilityId,
        csvString: csvLine,
      })
      .pipe(
        tap((res) => {
          console.log('addBed (importBedSetupCsv) RES:', res);
          this.closePopup();
          this.showSuccess("Bed added.");
          this.loadBedSetup();
          this.loadFacilitiesWithBedSetup();
        }),
        catchError((err) => {
          console.error('addBed ERR:', err);
          this.error = "Failed to add bed.";
          return of(null);
        }),
        finalize(() => this.cdr.markForCheck())
      )
      .subscribe();
  }

  // ---- CSV Import ----

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    this.importFiles = [];
    for (let i = 0; i < input.files.length; i++) {
      const file = input.files[i];
      const match = file.name.match(/fac_id_(\d+)/i);
      const fac_id = match ? Number(match[1]) : 0;
      this.importFiles.push({ file, fac_id, fileName: file.name });
    }

    input.value = "";
    this.importReplaceExisting = false;
    this.popupMode = "import";
    this.popupOpen = true;
    this.cdr.markForCheck();
  }

  canRunImport(): boolean {
    return !!this.importRegionId
        && this.importFiles.length > 0
        && this.importFiles.every((f) => !!f.fac_id);
  }

  async runImport() {
    if (!this.canRunImport()) {
      this.error = "All files must have a valid fac_id in the filename (e.g. fac_id_1.csv), and a region ID must be set.";
      return;
    }

    if (this.importReplaceExisting) {
      const fileCount = this.importFiles.length;
      const msg = `You are about to import ${fileCount} file${fileCount > 1 ? 's' : ''} with "Replace existing beds" enabled.\n\n`
        + `ANY BED THAT IS NOT IN THE CSV FILES WILL BE PERMANENTLY DELETED.\n\n`
        + `This action cannot be undone. Are you sure you want to continue?`;
      if (!confirm(msg)) {
        return;
      }
    }

    this.closePopup();
    this.loading = true;
    this.error = "";
    let totalInserted = 0;
    let totalUpdated = 0;
    let totalDeleted = 0;

    for (const item of this.importFiles) {
      try {
        const csvString = await this.readFileAsText(item.file);

        if (!this.hasDataRows(csvString)) {
          const reason = this.importReplaceExisting
            ? `${item.fileName}: file has no data rows — refusing to import (Replace mode would delete all beds for this facility). `
            : `${item.fileName}: file has no data rows — nothing to import. `;
          this.error += reason;
          continue;
        }

        console.log('importCsv REQ:', { fac_id: item.fac_id, fileName: item.fileName });
        const result = await this.server
          .postWithToken<any>("snfs/admin/importBedSetupCsv", {
            fac_id: item.fac_id,
            csvString,
            replaceExisting: this.importReplaceExisting,
          })
          .toPromise();
        console.log('importCsv RES:', result);
        totalInserted += result?.inserted || 0;
        totalUpdated += result?.updated || 0;
        totalDeleted += result?.deleted || 0;
      } catch (err) {
        console.error(`importCsv ERR for ${item.fileName}:`, err);
        this.error += `Failed: ${item.fileName}. `;
      }
    }

    this.showSuccess(`Import done: ${totalInserted} added, ${totalUpdated} updated, ${totalDeleted} removed across ${this.importFiles.length} files.`);
    this.loading = false;
    this.loadFacilitiesWithBedSetup();
    if (this.selectedFacilityId) this.loadBedSetup();
    this.cdr.markForCheck();
  }

  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }

  private hasDataRows(csvString: string): boolean {
    const lines = csvString.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
    return lines.length >= 2;
  }

  exportCsv() {
    if (!this.selectedFacilityId) return;
    console.log('exportCsv REQ:', { facIds: [this.selectedFacilityId] });
    this.server
      .postWithToken<{ fac_id: number; csv: string }[]>("snfs/admin/exportBedSetupCsv", {
        facIds: [this.selectedFacilityId],
      })
      .pipe(
        tap((results) => {
          console.log('exportCsv RES:', results);
          for (const item of results) {
            if (!item.csv) continue;
            const blob = new Blob([item.csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `bed_setup_fac_${item.fac_id}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }
        }),
        catchError((err) => {
          console.error('exportCsv ERR:', err);
          this.error = "Export failed.";
          return of(null);
        }),
        finalize(() => this.cdr.markForCheck())
      )
      .subscribe();
  }

  private showSuccess(msg: string) {
    this.successMsg = msg;
    setTimeout(() => {
      this.successMsg = "";
      this.cdr.markForCheck();
    }, 3000);
  }
}
